import type { ReaderWorkspace } from "../../../reader/reader-workspace";

export type ReaderDevice = "phone" | "tablet" | "desktop";
export type ReaderMarginPresetId = "compact" | "standard" | "relaxed";
export type ReaderTypographyPresetId = "level1" | "level2" | "level3" | "level4" | "level5" | "level6" | "level7" | "level8";
export type ReaderTypographySettings = Pick<ReaderWorkspace["preference"], "letterSpacing" | "lineHeight" | "paragraphSpacing">;
export type ReaderMarginPadding = Pick<
  ReaderWorkspace["preference"],
  "paddingTop" | "paddingRight" | "paddingBottom" | "paddingLeft"
>;

export type ReaderTypographyPreset = {
  id: ReaderTypographyPresetId;
  label: string;
  settings: ReaderTypographySettings;
};

export type ReaderMarginPreset = {
  id: ReaderMarginPresetId;
  label: string;
  padding: ReaderMarginPadding;
};

export function getReaderDevice(width: number): ReaderDevice {
  if (width < 640) return "phone";
  if (width < 1024) return "tablet";
  return "desktop";
}

export function getReaderTypographyPresets(device: ReaderDevice): Record<ReaderTypographyPresetId, ReaderTypographyPreset> {
  // 八档预设根据设备宽度调整 只负责字距行距段距
  const values: Record<ReaderDevice, ReaderTypographySettings[]> = {
    phone: [
      { letterSpacing: -0.2, lineHeight: 1.3, paragraphSpacing: 0 },
      { letterSpacing: 0, lineHeight: 1.45, paragraphSpacing: 6 },
      { letterSpacing: 0.2, lineHeight: 1.6, paragraphSpacing: 10 },
      { letterSpacing: 0.4, lineHeight: 1.75, paragraphSpacing: 14 },
      { letterSpacing: 0.6, lineHeight: 1.9, paragraphSpacing: 18 },
      { letterSpacing: 0.8, lineHeight: 2.05, paragraphSpacing: 22 },
      { letterSpacing: 1, lineHeight: 2.2, paragraphSpacing: 28 },
      { letterSpacing: 1.2, lineHeight: 2.4, paragraphSpacing: 34 },
    ],
    tablet: [
      { letterSpacing: -0.1, lineHeight: 1.35, paragraphSpacing: 0 },
      { letterSpacing: 0.1, lineHeight: 1.5, paragraphSpacing: 8 },
      { letterSpacing: 0.3, lineHeight: 1.65, paragraphSpacing: 14 },
      { letterSpacing: 0.5, lineHeight: 1.8, paragraphSpacing: 20 },
      { letterSpacing: 0.8, lineHeight: 1.95, paragraphSpacing: 26 },
      { letterSpacing: 1.1, lineHeight: 2.1, paragraphSpacing: 32 },
      { letterSpacing: 1.4, lineHeight: 2.3, paragraphSpacing: 40 },
      { letterSpacing: 1.8, lineHeight: 2.5, paragraphSpacing: 48 },
    ],
    desktop: [
      { letterSpacing: 0, lineHeight: 1.4, paragraphSpacing: 0 },
      { letterSpacing: 0.2, lineHeight: 1.55, paragraphSpacing: 10 },
      { letterSpacing: 0.5, lineHeight: 1.7, paragraphSpacing: 18 },
      { letterSpacing: 0.8, lineHeight: 1.85, paragraphSpacing: 26 },
      { letterSpacing: 1.1, lineHeight: 2, paragraphSpacing: 34 },
      { letterSpacing: 1.4, lineHeight: 2.2, paragraphSpacing: 42 },
      { letterSpacing: 1.8, lineHeight: 2.4, paragraphSpacing: 54 },
      { letterSpacing: 2.2, lineHeight: 2.65, paragraphSpacing: 66 },
    ],
  };

  return Object.fromEntries(
    values[device].map((settings, index) => {
      const id = `level${index + 1}` as ReaderTypographyPresetId;

      return [id, { id, label: `${index + 1}档`, settings }];
    }),
  ) as Record<ReaderTypographyPresetId, ReaderTypographyPreset>;
}

export function getSelectedTypographyPreset(
  preference: ReaderWorkspace["preference"],
  presets: Record<ReaderTypographyPresetId, ReaderTypographyPreset>,
): ReaderTypographyPresetId | "custom" {
  const matchedPreset = Object.values(presets).find(preset =>
    Math.abs(preset.settings.letterSpacing - preference.letterSpacing) < 0.001 &&
    Math.abs(preset.settings.lineHeight - preference.lineHeight) < 0.001 &&
    preset.settings.paragraphSpacing === preference.paragraphSpacing,
  );

  return matchedPreset?.id ?? "custom";
}

export function getReaderMarginPresets(device: ReaderDevice): Record<ReaderMarginPresetId, ReaderMarginPreset> {
  const presets: Record<ReaderDevice, Record<ReaderMarginPresetId, ReaderMarginPreset>> = {
    phone: {
      compact: { id: "compact", label: "紧凑", padding: createReaderMarginPadding(28, 24) },
      standard: { id: "standard", label: "标准", padding: createReaderMarginPadding(16, 18) },
      relaxed: { id: "relaxed", label: "宽松", padding: createReaderMarginPadding(8, 10) },
    },
    tablet: {
      compact: { id: "compact", label: "紧凑", padding: createReaderMarginPadding(44, 112) },
      standard: { id: "standard", label: "标准", padding: createReaderMarginPadding(28, 72) },
      relaxed: { id: "relaxed", label: "宽松", padding: createReaderMarginPadding(16, 40) },
    },
    desktop: {
      compact: { id: "compact", label: "紧凑", padding: createReaderMarginPadding(64, 260) },
      standard: { id: "standard", label: "标准", padding: createReaderMarginPadding(40, 176) },
      relaxed: { id: "relaxed", label: "宽松", padding: createReaderMarginPadding(24, 96) },
    },
  };

  return presets[device];
}

export function getSelectedMarginPreset(
  preference: ReaderWorkspace["preference"],
  presets: Record<ReaderMarginPresetId, ReaderMarginPreset>,
): ReaderMarginPresetId | "custom" {
  const matchedPreset = Object.values(presets).find(preset =>
    preset.padding.paddingTop === preference.paddingTop &&
    preset.padding.paddingRight === preference.paddingRight &&
    preset.padding.paddingBottom === preference.paddingBottom &&
    preset.padding.paddingLeft === preference.paddingLeft,
  );

  return matchedPreset?.id ?? "custom";
}

function createReaderMarginPadding(vertical: number, horizontal: number): ReaderMarginPadding {
  return {
    paddingTop: vertical,
    paddingRight: horizontal,
    paddingBottom: vertical,
    paddingLeft: horizontal,
  };
}
